import { Button } from '@tempo-io/sui-button';
import { Checkbox } from '@tempo-io/sui-checkbox';
import { cn } from '@tempo-io/sui-class-utils';
import { Close, LeftPanel } from '@tempo-io/sui-icons';
import { Input } from '@tempo-io/sui-input';
import { ScrollArea } from '@tempo-io/sui-scrollarea';
import { Separator } from '@tempo-io/sui-separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@tempo-io/sui-sheet';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useFlags } from '@/lib/hooks/feature-flags/useFlagsWithOverrides';
import { useFeatureFlagOverrides } from '@/lib/providers/featureFlagOverrideContext';
import type { FlagKeys } from '@/lib/providers/featureFlagProvider';

const SIDE_STORAGE_KEY = '__nexus_devtools_side__';

interface FeatureFlagDevToolsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const loadSide = (): 'left' | 'right' => {
  const stored = localStorage.getItem(SIDE_STORAGE_KEY);
  if (stored === 'left' || stored === 'right') {
    return stored;
  }
  return 'right';
};

const saveSide = (side: 'left' | 'right'): void => {
  localStorage.setItem(SIDE_STORAGE_KEY, side);
};

export const FeatureFlagDevToolsDialog: React.FC<FeatureFlagDevToolsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const ldFlags = useFlags<FlagKeys>();
  const { overrides, setFlagOverride, clearFlagOverride, clearAllOverrides } =
    useFeatureFlagOverrides();
  const [searchQuery, setSearchQuery] = useState('');
  const [flashingFlag, setFlashingFlag] = useState<string | null>(null);
  const [copiedFlag, setCopiedFlag] = useState<string | null>(null);
  const [side, setSide] = useState<'left' | 'right'>(loadSide);

  // Get all flag names sorted - overridden flags first, then alphabetically
  const { flagNames, overriddenFlags, nonOverriddenFlags } = useMemo(() => {
    const allFlags = Object.keys(ldFlags) as Array<keyof FlagKeys>;

    // Separate overridden and non-overridden flags
    const overriddenFlags = allFlags.filter((flag) => flag in overrides).sort();
    const nonOverriddenFlags = allFlags.filter((flag) => !(flag in overrides)).sort();

    // Overridden flags first, then the rest
    return {
      flagNames: [...overriddenFlags, ...nonOverriddenFlags],
      overriddenFlags,
      nonOverriddenFlags,
    };
  }, [ldFlags, overrides]);

  // Filter flags based on search query
  const filteredFlagNames = useMemo(() => {
    if (!searchQuery.trim()) {
      return flagNames;
    }
    const query = searchQuery.toLowerCase();
    return flagNames.filter((flagName) => String(flagName).toLowerCase().includes(query));
  }, [flagNames, searchQuery]);

  // Prepare filtered flag data for rendering
  const flagsToRender = useMemo(() => {
    return filteredFlagNames
      .map((flagName, index) => {
        const ldValue = ldFlags[flagName];
        const hasOverride = flagName in overrides;
        const currentValue = hasOverride ? overrides[flagName] : ldValue;

        // Skip non-boolean flags (like string flags)
        if (typeof ldValue !== 'boolean') {
          return null;
        }

        // Show separator when transitioning from overridden to non-overridden flags
        const showSeparator =
          !searchQuery.trim() &&
          index === overriddenFlags.length &&
          overriddenFlags.length > 0 &&
          nonOverriddenFlags.length > 0;

        return {
          flagName,
          ldValue,
          hasOverride,
          currentValue,
          showSeparator,
        };
      })
      .filter(Boolean);
  }, [
    filteredFlagNames,
    ldFlags,
    overrides,
    searchQuery,
    overriddenFlags.length,
    nonOverriddenFlags.length,
  ]);

  const handleToggle = (flagName: keyof FlagKeys, checked: boolean) => {
    const ldValue = ldFlags[flagName];

    if (checked === ldValue) {
      // If the new value matches LaunchDarkly's value, clear the override
      clearFlagOverride(flagName);
    } else {
      // Otherwise, set the override
      setFlagOverride(flagName, checked);
    }

    // Flash animation feedback
    setFlashingFlag(String(flagName));
    setTimeout(() => setFlashingFlag(null), 300);
  };

  const handleResetAll = () => {
    clearAllOverrides();
  };

  const handleCopyFlagName = async (flagName: string) => {
    await navigator.clipboard.writeText(flagName);
    setCopiedFlag(flagName);
    setTimeout(() => setCopiedFlag(null), 2000);
  };

  // Persist side preference to localStorage
  useEffect(() => {
    saveSide(side);
  }, [side]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side={side}
        className={cn(
          'w-[550px] sm:max-w-[550px] bg-bg-neutral-bold text-text-neutral-inverted flex flex-col p-0',
          side === 'left' ? 'border-r-2 border-border-warning' : 'border-l-2 border-border-warning',
        )}
      >
        <SheetHeader className="px-6 pt-6 pb-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-bg-warning text-text-neutral-primary text-xs font-bold rounded">
              DEV TOOLS
            </span>
            <SheetTitle className="text-text-neutral-inverted font-mono">
              Feature Flag Overrides
            </SheetTitle>
          </div>
          <SheetDescription className="text-text-neutral-secondary font-mono text-sm">
            Override feature flags locally without changing LaunchDarkly. Changes persist in
            localStorage.
          </SheetDescription>
        </SheetHeader>

        <Separator className="bg-bg-neutral" />

        <div className="px-6 py-4">
          <div className="relative flex items-center">
            <Input
              type="text"
              placeholder="Search flags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="dark pr-10 w-full"
            />
            {searchQuery && (
              <Button
                onClick={() => setSearchQuery('')}
                variant="ghost"
                size="icon"
                className="absolute right-2 h-8 w-8"
                title="Clear search"
                type="button"
              >
                <Close className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden px-6">
          <ScrollArea className="h-full pr-4">
            <div className="space-y-2">
              {overriddenFlags.length > 0 && !searchQuery.trim() && (
                <div className="flex items-center gap-2 py-2">
                  <Separator className="flex-1 bg-bg-neutral-bold" />
                  <span className="text-xs text-text-neutral font-mono">Overridden flags</span>
                  <Separator className="flex-1 bg-bg-neutral-bold" />
                </div>
              )}
              {flagsToRender.length === 0 ? (
                <div className="text-center py-8 text-text-neutral font-mono text-sm">
                  No flags found matching &quot;{searchQuery}&quot;
                </div>
              ) : null}
              {flagsToRender.map((flag) => {
                if (!flag) return null;
                const { flagName, ldValue, hasOverride, currentValue, showSeparator } = flag;

                return (
                  <Fragment key={String(flagName)}>
                    {showSeparator && (
                      <div className="flex items-center gap-2 py-2">
                        <Separator className="flex-1 bg-bg-neutral-bold" />
                        <span className="text-xs text-text-neutral-inverted font-mono">
                          Other flags
                        </span>
                        <Separator className="flex-1 bg-bg-neutral-bold" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'flex items-center justify-between p-3 rounded-md bg-bg-neutral-bold border border-border-neutral hover:border-border-neutral-subtle transition-all duration-300',
                        flashingFlag === String(flagName) &&
                          'bg-bg-warning/20 border-border-warning',
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Checkbox
                          id={String(flagName)}
                          checked={currentValue as boolean}
                          onCheckedChange={(checked) => handleToggle(flagName, checked as boolean)}
                          className="border-border-neutral"
                        />
                        <label
                          htmlFor={String(flagName)}
                          className="flex-1 cursor-pointer select-none"
                        >
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="font-mono text-sm text-text-neutral-inverted cursor-pointer hover:text-text-warning transition-colors bg-transparent border-none p-0"
                              onClick={(e) => {
                                e.preventDefault();
                                handleCopyFlagName(String(flagName));
                              }}
                              title="Click to copy flag name"
                            >
                              {String(flagName)}
                            </button>
                            {copiedFlag === String(flagName) && (
                              <span className="text-xs text-text-success font-mono animate-in fade-in duration-200">
                                Copied!
                              </span>
                            )}
                            {hasOverride && (
                              <span className="px-1.5 py-0.5 bg-bg-warning text-text-neutral-primary text-xs font-bold rounded">
                                OVERRIDE
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-text-neutral-secondary font-mono mt-0.5">
                            LaunchDarkly: {ldValue ? 'true' : 'false'}
                          </div>
                        </label>
                      </div>
                      {hasOverride && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            clearFlagOverride(flagName);
                          }}
                          className="h-8 w-8 shrink-0"
                          title="Clear override"
                          type="button"
                        >
                          <Close className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <div className="px-6 py-4 border-t border-border-neutral mt-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-xs text-text-neutral-secondary font-mono">
                {Object.keys(overrides).length} override(s) active
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSide('left')}
                  className={cn(
                    'h-7 w-7',
                    side === 'left'
                      ? 'bg-bg-warning text-text-neutral-primary hover:bg-bg-warning-hovered hover:text-text-neutral-primary active:bg-bg-warning-pressed'
                      : 'text-text-neutral-secondary hover:text-text-neutral-primary hover:bg-bg-warning-hovered active:bg-bg-warning-pressed',
                  )}
                  title="Dock to left"
                  type="button"
                >
                  <LeftPanel className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSide('right')}
                  className={cn(
                    'h-7 w-7',
                    side === 'right'
                      ? 'bg-bg-warning text-text-neutral-primary hover:bg-bg-warning-hovered hover:text-text-neutral-primary active:bg-bg-warning-pressed'
                      : 'text-text-neutral-secondary hover:text-text-neutral-primary hover:bg-bg-warning-hovered active:bg-bg-warning-pressed',
                  )}
                  title="Dock to right"
                  type="button"
                >
                  <LeftPanel className="w-4 h-4 scale-x-[-1]" />
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleResetAll}
                disabled={Object.keys(overrides).length === 0}
                className="border-border-neutral text-text-neutral-primary hover:bg-bg-neutral hover:text-text-neutral-primary disabled:opacity-50"
              >
                Reset All
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-bg-warning text-text-neutral-primary hover:bg-bg-warning-hovered hover:text-text-neutral-primary active:bg-bg-warning-pressed"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
